import { useEffect, useState } from "react";
import { CaretLeft, CaretRight, Pause, Play, Stop, Check, X, Sparkle } from "@phosphor-icons/react";
import { onPipState, onPipLevel, sendPipAction, startPipDrag, setPipBounds } from "../lib/native";

/**
 * The floating companion rendered as a macOS Dynamic-Island-style overlay,
 * anchored at the top-center of the screen in its own frameless/transparent
 * always-on-top window.
 *
 *   - Collapsed (default): a slim black pill with a "Lisning" waveform plus
 *     pause + stop — the resting state while nothing is worth surfacing.
 *   - Expanded (a question arrived): the pill grows into a dark panel showing
 *     the question with prev/next navigation and Asked-it / Skip actions.
 *
 * The overlay sizes and re-centers ITS OWN window between the two states (the
 * window hugs the content so transparent areas don't eat clicks). State arrives
 * via `pip:state`; button presses go back via `pip:action`.
 */

// Window bounds for each state (logical px). Window hugs the content.
// Collapsed is wide so its left/right clusters clear the centered notch; the
// center stays empty (black, merges with the notch). Expanded reserves a top
// strip so all content drops BELOW the notch line, never occluded.
const COLLAPSED = { w: 640, h: 38 };
const EXPANDED = { w: 720, h: 252 };
// Approx physical notch on Apple Silicon MacBooks (logical px). Used to keep
// content out from behind it. Harmless on non-notch displays (just dead space).
const NOTCH_W = 230;
const NOTCH_H = 40;

// Live waveform — bar heights are driven by the real audio `level` (0..1)
// streamed from the capture sidecar, smoothed via a short CSS transition.
function Waveform({ level = 0, color = "rgba(255,255,255,0.95)" }) {
    const weights = [0.4, 0.7, 0.35, 1.0, 0.6, 0.85, 0.5, 0.78, 0.4, 0.95, 0.55, 0.8, 0.35, 0.65, 0.45];
    const maxH = 16;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 2.5, height: maxH }} aria-hidden="true">
            {weights.map((w, i) => (
                <span
                    key={i}
                    style={{
                        width: 2.5,
                        height: 2.5 + Math.min(maxH, w * level * (maxH + 5)),
                        borderRadius: 3,
                        background: color,
                        transition: "height 90ms ease-out",
                    }}
                />
            ))}
        </div>
    );
}

function CircleBtn({ bg, shadow, onClick, label, children, size = 40 }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            style={{
                width: size, height: size, flexShrink: 0,
                borderRadius: "50%", border: "none",
                background: bg, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", boxShadow: shadow,
            }}
        >
            {children}
        </button>
    );
}

export default function PipOverlay() {
    const [s, setS] = useState({
        question: "", why: "", index: 0, total: 0, recording: true, paused: false,
    });

    // The companion window must be see-through — strip the app's white page
    // background so only the black island paints.
    useEffect(() => {
        const prev = {
            htmlBg: document.documentElement.style.background,
            bodyBg: document.body.style.background,
        };
        document.documentElement.style.background = "transparent";
        document.body.style.background = "transparent";
        document.body.style.margin = "0";
        const root = document.getElementById("root");
        if (root) root.style.background = "transparent";
        return () => {
            document.documentElement.style.background = prev.htmlBg;
            document.body.style.background = prev.bodyBg;
        };
    }, []);

    const [level, setLevel] = useState(0);

    useEffect(() => {
        let un = () => {};
        (async () => { un = await onPipState((next) => setS((p) => ({ ...p, ...next }))); })();
        return () => { try { un(); } catch { /* ignore */ } };
    }, []);

    // Live audio level (0..1) streamed from the capture sidecar.
    useEffect(() => {
        let un = () => {};
        (async () => { un = await onPipLevel((v) => setLevel(v)); })();
        return () => { try { un(); } catch { /* ignore */ } };
    }, []);

    const expanded = !!s.question;

    // Grow/shrink the window to match the layout, and keep it docked top-center.
    useEffect(() => {
        const b = expanded ? EXPANDED : COLLAPSED;
        setPipBounds(b.w, b.h, 0);
    }, [expanded]);

    const hasPrev = s.index > 0;
    const hasNext = s.total > 0 && s.index < s.total - 1;
    const animating = s.recording && !s.paused;

    const wrap = {
        boxSizing: "border-box",
        width: "100vw", height: "100vh",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "0",
        background: "transparent",
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        WebkitUserSelect: "none", userSelect: "none",
    };

    // The black island. Flat top corners (0) so it reads as descending straight
    // from the top bezel / notch; only the bottom corners are rounded.
    const island = {
        position: "relative",
        width: "100%",
        background: "#0A0A0B",
        color: "#fff",
        borderRadius: expanded ? "0 0 28px 28px" : "0 0 16px 16px",
        // No shadow on the resting bar (it read as a halo over the menu bar);
        // the expanded panel gets a soft downward shadow inline below.
        boxShadow: expanded ? "0 18px 44px -16px rgba(0,0,0,0.55)" : "none",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        overflow: "hidden",
    };

    const btnSize = expanded ? 40 : 28;
    const pauseBtn = (
        <CircleBtn
            bg="#7E2A2A"
            shadow="0 2px 8px rgba(126,42,42,0.45)"
            size={btnSize}
            onClick={() => sendPipAction(s.paused ? "resume" : "pause")}
            label={s.paused ? "Resume" : "Pause"}
        >
            {s.paused ? <Play size={expanded ? 15 : 13} weight="fill" /> : <Pause size={expanded ? 15 : 13} weight="fill" />}
        </CircleBtn>
    );
    const stopBtn = (
        <CircleBtn
            bg="#26262A"
            shadow="0 2px 8px rgba(0,0,0,0.4)"
            size={btnSize}
            onClick={() => sendPipAction("stop")}
            label="Stop & write up"
        >
            <Stop size={expanded ? 14 : 12} weight="fill" />
        </CircleBtn>
    );

    // ── Collapsed: slim resting pill ───────────────────────────────────────
    if (!expanded) {
        return (
            <div style={wrap}>
                <style>{`@keyframes pipWave {0%,100%{transform:scaleY(0.4)}50%{transform:scaleY(1)}} @keyframes pipDot {0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
                <div
                    style={{ ...island, height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px" }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) startPipDrag(); }}
                >
                    {/* Left cluster — sits left of the notch */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#FF3B30", animation: animating ? "pipDot 1.4s ease-in-out infinite" : "none", flexShrink: 0 }} />
                        <Waveform level={animating ? level : 0} />
                    </div>
                    {/* Center notch keep-out (empty, merges with the notch) */}
                    <div style={{ width: NOTCH_W, flexShrink: 0 }} onMouseDown={startPipDrag} />
                    {/* Right cluster — sits right of the notch */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {pauseBtn}
                        {stopBtn}
                    </div>
                </div>
            </div>
        );
    }

    // ── Expanded: question panel ───────────────────────────────────────────
    const chev = (enabled) => ({
        width: 32, height: 32, flexShrink: 0, borderRadius: "50%", border: "none",
        background: enabled ? "rgba(255,255,255,0.08)" : "transparent",
        color: enabled ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.22)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: enabled ? "pointer" : "default",
    });
    const tabBtn = (accent) => ({
        flex: 1, height: 38, borderRadius: 12, border: "none",
        background: "rgba(255,255,255,0.06)",
        color: accent || "rgba(255,255,255,0.92)",
        fontSize: 13, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        cursor: "pointer",
    });

    return (
        <div style={wrap}>
            <style>{`@keyframes pipWave {0%,100%{transform:scaleY(0.4)}50%{transform:scaleY(1)}}`}</style>
            <div style={{ ...island, padding: `${NOTCH_H + 10}px 16px 14px` }}>
                {/* Question row with prev/next */}
                <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) startPipDrag(); }}
                >
                    <button type="button" style={chev(hasPrev)} disabled={!hasPrev} onClick={() => hasPrev && sendPipAction("prev")} aria-label="Previous">
                        <CaretLeft size={16} weight="bold" />
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                            <Sparkle size={11} weight="fill" color="#FF6B6B" />
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: "#9A9AA2", textTransform: "uppercase" }}>
                                Worth asking
                            </span>
                            {s.total > 1 && (
                                <span style={{ marginLeft: "auto", fontSize: 11, color: "#6b6b73", fontFamily: 'ui-monospace, monospace' }}>
                                    {s.index + 1}/{s.total}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 19, lineHeight: 1.26, fontWeight: 600, letterSpacing: "-0.01em", color: "#fff", maxHeight: 74, overflow: "hidden" }}>
                            {s.question}
                        </div>
                    </div>
                    <button type="button" style={chev(hasNext)} disabled={!hasNext} onClick={() => hasNext && sendPipAction("next")} aria-label="Next">
                        <CaretRight size={16} weight="bold" />
                    </button>
                </div>

                {/* Control row: waveform + Asked / Skip + pause + stop */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
                    <div style={{ flexShrink: 0, opacity: 0.85 }}><Waveform level={animating ? level : 0} /></div>
                    <button type="button" style={tabBtn("#fff")} onClick={() => sendPipAction("ask")}>
                        <Check size={14} weight="bold" /> Asked it
                    </button>
                    <button type="button" style={tabBtn("#A1A1AA")} onClick={() => sendPipAction("skip")}>
                        <X size={13} weight="bold" /> Skip
                    </button>
                    {pauseBtn}
                    {stopBtn}
                </div>
            </div>
        </div>
    );
}
